import { css } from '@emotion/react';
import { create } from 'zustand';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { SettingsSection } from '@frontend/settings-ui';
import { theme } from '@frontend/theme';
import { Heading, Slider, Text, WEAVE_FONT_SCALE_KEY } from '@frontend/design-system';

const DEFAULT_SCALE = 1;
const MIN_SCALE = 0.8;
const MAX_SCALE = 1.3;
const SCALE_STEP = 0.01;

const styles = {
  heading: css`
    margin: ${theme.spacing(2, 0)};
  `,
  sectionTitle: css`
    margin-bottom: ${theme.spacing(3)};
  `,
  previewContainer: css`
    width: 100%;
    padding: ${theme.spacing(5, 0)};
    display: flex;
    justify-content: center;
    border: 1px solid ${theme.colors.neutral90};
    border-radius: ${theme.borderRadius.medium};
    background-color: ${theme.colors.neutral5};
  `,
  previewCard: css`
    padding: ${theme.spacing(3)};
    border-radius: ${theme.borderRadius.medium};
    background-color: ${theme.colors.white};
    max-width: 370px;
  `,
  previewContent: css`
    border-radius: ${theme.borderRadius.medium};
    overflow: hidden;
    border: 1px solid ${theme.colors.neutral20};
  `,
  previewHeader: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
    padding: ${theme.spacing(2.5, 2.5, 2.5, 2)};
    background-color: ${theme.colors.neutral5};
  `,
  previewBody: css`
    padding: ${theme.spacing(2)};
  `,
  sliderContainer: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(2)};
    width: 100%;
    margin-top: ${theme.spacing(1)};
  `,
  slider: css`
    flex: 1;
  `,
};

const getScaledFontSize = (scale: number, multiplier = 1) => `${scale * 100 * multiplier}%`;

const formatSliderValue = (value: number) => `${(value * 100).toFixed(0)}%`;

const FontSizePreview = ({ scale }: { scale: number }) => {
  const { t } = useTranslation('base');

  return (
    <div css={styles.previewContainer}>
      <div css={styles.previewCard}>
        <div css={styles.previewContent}>
          <header css={styles.previewHeader}>
            <Heading level={3} style={{ fontSize: getScaledFontSize(scale, 2) }}>
              Transcript
            </Heading>
            <Icon name='caret-up' />
          </header>
          <div css={styles.previewBody}>
            <Text size='large' style={{ fontSize: getScaledFontSize(scale) }}>
              {t(
                `Hi this is Amelia Walker I have an appointment scheduled for tomorrow and I'm not going to make it. I was hoping I could reschedule for next week, please give me a call back on my cell phone, thank you.`
              )}
            </Text>
          </div>
        </div>
      </div>
    </div>
  );
};

export const FontSizeCustomizer = () => {
  const { t } = useTranslation('base');
  const scale = useFontSizeCustomizerStore((state) => state.scale);
  const setScale = useFontSizeCustomizerStore((state) => state.setScale);
  const effectiveScale = scale ?? DEFAULT_SCALE;

  return (
    <>
      <Heading level={3} css={styles.heading}>
        {t('Font Preferences')}
      </Heading>

      <SettingsSection>
        <h3 css={styles.sectionTitle}>{t('Font Size')}</h3>
        <FontSizePreview scale={effectiveScale} />
        <div css={styles.sliderContainer}>
          <Text weight='bold'>Font Size</Text>
          <Slider
            css={styles.slider}
            min={MIN_SCALE}
            max={MAX_SCALE}
            step={SCALE_STEP}
            value={effectiveScale}
            onChange={setScale}
            aria-label='Font Size'
            formatValue={formatSliderValue}
          />
        </div>
      </SettingsSection>
    </>
  );
};

interface FontSizeCustomizerStore {
  scale: number | null;
  hasScaleChanged: boolean;
  setScale: (newScale: number) => void;
  handleScaleApply: () => void;
  resetScale: () => void;
}

const getPersistedScale = (): number | null => {
  const saved = localStorage.getItem(WEAVE_FONT_SCALE_KEY);
  return saved ? parseFloat(saved) : null;
};

const persistScale = (scale: number): void => {
  localStorage.setItem(WEAVE_FONT_SCALE_KEY, String(scale));
};

const applyScaleToDocument = (scale: number): void => {
  document.documentElement.style.fontSize = `${scale * 100}%`;
};

export const useFontSizeCustomizerStore = create<FontSizeCustomizerStore>((set, get) => ({
  scale: getPersistedScale(),
  hasScaleChanged: false,

  setScale: (newScale) => {
    const persistedScale = getPersistedScale();
    const hasChanged = persistedScale !== newScale;
    set({ scale: newScale, hasScaleChanged: hasChanged });
  },

  handleScaleApply: () => {
    const { scale } = get();
    if (scale === null) return;

    applyScaleToDocument(scale);
    persistScale(scale);
    set({ hasScaleChanged: false });
  },

  resetScale: () => {
    const { scale } = get();
    if (scale === null || scale === DEFAULT_SCALE) {
      set({ scale: null, hasScaleChanged: false });
      return;
    }

    const persistedScale = getPersistedScale();
    set({ scale: persistedScale, hasScaleChanged: false });
  },
}));
